import { createApi } from '@reduxjs/toolkit/query/react';
import { CacheTags, PaginatedRequest, SomeRequest } from 'types';
import { AfeSearchRecord } from './bindings/AfeSearchRecord';
import { AfeResponse } from './bindings/AfeResponse';
import { BoardProjectSearchRecord } from './bindings/BoardProjectSearchRecord';
import { BoardProjectResponse } from './bindings/BoardProjectResponse';
import { PostAfeRequest } from './bindings/PostAfeRequest';
import { PutAfeRequest } from './bindings/PutAfeRequest';

import { FetchResponse } from './bindings/FetchResponse';
import { HourSeconds, OdinBaseQuery } from './core';

type DocumentResponse = { url: string };
type DocumentRequest = { afeId: string; fileName: string; clientId: string };
export const afesApi = createApi({
  reducerPath: 'afes',
  baseQuery: OdinBaseQuery,
  tagTypes: [CacheTags.afes, CacheTags.afeDetail],
  endpoints: (builder) => ({
    fetchSomeBoardProjects: builder.query<
      BoardProjectSearchRecord[],
      SomeRequest
    >({
      query: (payload) => ({
        url: '/api/board-projects',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => {
        if (!payload?.length) {
          return [];
        }
        return payload.map((bp) => ({
          type: CacheTags.boardProjects,
          id: bp.id,
        }));
      },
    }),
    fetchBoardProjectPage: builder.query<
      FetchResponse<BoardProjectResponse>,
      PaginatedRequest
    >({
      query: (payload) => ({
        url: '/api/board-projects',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => {
        if (!payload) {
          return [];
        }
        return payload.items.map((bp) => ({
          type: CacheTags.boardProjects,
          id: bp.id,
        }));
      },
    }),
    fetchBoardProjectDetail: builder.query<BoardProjectResponse, string>({
      query: (payload) => ({
        url: `/api/board-projects/${payload}`,
        method: 'GET',
      }),
      providesTags: (payload) => {
        if (!payload) {
          return [];
        }
        return [{ type: CacheTags.boardProjectDetail, id: payload.id }];
      },
    }),
    fetchAfeDetails: builder.query<AfeResponse, string>({
      query: (payload) => ({
        url: `/api/afes/${payload}`,
        method: 'GET',
      }),
      providesTags: (payload) => {
        if (!payload) {
          return [];
        }
        return [{ type: CacheTags.afeDetail, id: payload.id }];
      },
    }),
    fetchSomeAfes: builder.query<FetchResponse<AfeSearchRecord>, SomeRequest>({
      query: (payload) => ({
        url: '/api/afes',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => [
        ...(payload?.items.map((a) => ({
          type: CacheTags.afes,
          id: a.id,
        })) || []),
      ],
    }),
    fetchAfesPage: builder.query<
      FetchResponse<AfeSearchRecord>,
      PaginatedRequest
    >({
      keepUnusedDataFor: HourSeconds,
      query: (payload) => ({
        url: '/api/afes',
        method: 'GET',
        params: payload,
      }),
      providesTags: (data) => {
        let returnValue = [{ type: CacheTags.afes, id: CacheTags.partial }];

        if (data) {
          returnValue = [
            ...returnValue,
            ...data.items.map(({ id }) => ({
              type: CacheTags.afes,
              id: id,
            })),
          ];
        }

        return returnValue;
      },
    }),
    postAfe: builder.mutation<AfeResponse, PostAfeRequest>({
      query: (payload) => ({
        url: '/api/afes',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result) => [
        {
          type: CacheTags.afes,
          id: CacheTags.partial,
        },
        {
          type: CacheTags.afeDetail,
          id: result?.id ?? CacheTags.partial,
        },
      ],
    }),
    putAfe: builder.mutation<AfeResponse, PutAfeRequest>({
      query: (payload) => ({
        url: `/api/afes/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        {
          type: CacheTags.afes,
          id: payload.id,
        },
        {
          type: CacheTags.afeDetail,
          id: payload.id,
        },
      ],
    }),
    fetchAfeDocumentDownloadLink: builder.query<
      DocumentResponse,
      DocumentRequest
    >({
      query: (payload) => ({
        url: `/api/afes/${payload.afeId}/documents/${payload.fileName}?clientId=${payload.clientId}`,
        method: 'GET',
        redirect: 'manual',
      }),
    }),
    putAfeEacLineItems: builder.mutation<AfeResponse, any>({
      query: (payload) => ({
        url: `/api/afes/${payload.afeId}/eac-line-items`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.afeDetail, id: payload.afeId },
      ],
    }),
    postAfeDocumentUploadLink: builder.mutation<
      DocumentResponse,
      DocumentRequest
    >({
      query: (payload) => ({
        url: `/api/afes/${payload.afeId}/documents/${payload.fileName}?clientId=${payload.clientId}`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useFetchAfesPageQuery,
  useFetchSomeAfesQuery,
  useFetchAfeDetailsQuery,
  useFetchSomeBoardProjectsQuery,
  useFetchBoardProjectDetailQuery,
  useFetchBoardProjectPageQuery,
  usePostAfeMutation,
  usePutAfeMutation,
  usePostAfeDocumentUploadLinkMutation,
  useFetchAfeDocumentDownloadLinkQuery,
} = afesApi;
